import lodashGet from 'lodash.get';
import { useMemo } from 'react';
import { selectCurrency } from '@/entities/session';
import { useCompactNumberFormatter, useNumberFormatter } from '@/shared/hooks';
import { useAppSelector } from '@/shared/model';
import { toFormatType } from '@/shared/types';
import type {
  MetricColumnDataType,
  MetricDataFormatType,
} from '@/shared/types';
import { NO_AXIS_SELECTED_VALUE } from '../../model/constants';
import type { Chart, ChartMeta } from '../../model/types';
import type { DataPoint, YAxisParams } from './types';

interface Params<T extends string> {
  meta?: ChartMeta | null;
  chart: Chart;
  data: DataPoint<T>[];
  splitOptions?: string[];
  defaultSplitOption: string;
  excludedSplitOptions: string[];
  tooltipMetrics?: string[];
}

interface Line {
  name: string;
  value: string;
}

interface LineChartParams<T extends string> {
  lines: Line[];
  leftY?: YAxisParams;
  rightY?: YAxisParams;
  tooltipYs?: YAxisParams[];
  data: DataPoint<T>[];
  noData: boolean;
}

export function useLineChartParams<T extends string>({
  meta,
  chart,
  data,
  splitOptions,
  defaultSplitOption,
  excludedSplitOptions,
  tooltipMetrics,
}: Params<T>): LineChartParams<T> {
  const userCurrency = useAppSelector(selectCurrency);
  const formattersMap = useNumberFormatter(userCurrency);
  const compactFormattersMap = useCompactNumberFormatter(userCurrency);
  const lines = useMemo(() => {
    return getChartLines(
      chart.splitDimension,
      defaultSplitOption,
      splitOptions,
    );
  }, [chart, splitOptions, defaultSplitOption]);

  if (!meta) {
    return {
      noData: true,
      lines: [],
      data: [],
    };
  }

  const leftY = getYAxisParams({
    meta,
    metric: chart.primaryMetric,
    data,
    lines,
    excludedLines: excludedSplitOptions,
    formattersMap,
    compactFormattersMap,
  });
  const rightY =
    chart.secondaryMetric !== NO_AXIS_SELECTED_VALUE
      ? getYAxisParams({
          meta,
          metric: chart.secondaryMetric,
          data,
          lines,
          excludedLines: excludedSplitOptions,
          formattersMap,
          compactFormattersMap,
        })
      : undefined;

  const tooltipYs = (tooltipMetrics ?? [])
    .map((metric) =>
      getYAxisParams({
        meta,
        metric,
        data,
        lines,
        excludedLines: excludedSplitOptions,
        formattersMap,
        compactFormattersMap,
      }),
    )
    .filter((axis): axis is YAxisParams => !!axis);

  return {
    noData: (!leftY || leftY.noData) && (!rightY || rightY.noData),
    lines,
    leftY,
    rightY,
    tooltipYs,
    data,
  };
}

const getChartLines = (
  split: string,
  defaultSplit: string,
  options?: string[],
): Line[] => {
  if (split === defaultSplit) {
    return [{ name: '', value: defaultSplit }];
  }

  return (options || []).map((option) => {
    return {
      name: option,
      value: option,
    };
  });
};

const getYAxisParams = ({
  meta,
  metric,
  data,
  lines,
  excludedLines,
  formattersMap,
  compactFormattersMap,
}: {
  meta: ChartMeta;
  metric: string;
  data: unknown[];
  lines: Line[];
  excludedLines: string[];
  formattersMap: ReturnType<typeof useNumberFormatter>;
  compactFormattersMap: ReturnType<typeof useCompactNumberFormatter>;
}): YAxisParams | undefined => {
  if (!meta.config[metric]) {
    return undefined;
  }

  const DEFAULT_MAX = 1;
  const DEFAULT_MIN = 0;
  const metricConfig = meta.config[metric];
  const dataTypeKey = metricConfig.type as MetricColumnDataType;
  const formatTypeKey = toFormatType(
    metricConfig.dataType as MetricDataFormatType,
  );
  const valueFormatter = formattersMap[dataTypeKey][formatTypeKey];
  const tickFormatter = compactFormattersMap[dataTypeKey][formatTypeKey];
  const getDataProp = (lineProp: string) => `data['${lineProp}'].${metric}`;
  const noData = data.every((row) => {
    return lines.every((line) => {
      return lodashGet(row, getDataProp(line.value)) === undefined;
    });
  });
  const excludedLinesSet = new Set(excludedLines);

  return {
    unit: metric,
    maxLabelLength: data.reduce<number>((acc, row) => {
      return Math.max(
        acc,
        ...lines.map((line) => {
          return tickFormatter(lodashGet(row, getDataProp(line.value), 0))
            .length;
        }),
      );
    }, 0),
    noData,
    dataPropLabel: metricConfig.name,
    tickFormatter,
    valueFormatter,
    getDataProp,
    maxValue: noData
      ? DEFAULT_MAX
      : data.reduce<number>((acc, row) => {
          return Math.max(
            acc,
            ...lines.map((line) => {
              if (excludedLinesSet.has(line.value)) {
                return -Infinity;
              }

              return lodashGet(row, getDataProp(line.value), -Infinity);
            }),
          );
        }, -Infinity),
    minValue: noData
      ? DEFAULT_MIN
      : data.reduce<number>((acc, row) => {
          return Math.min(
            acc,
            ...lines.map((line) => {
              if (excludedLinesSet.has(line.value)) {
                return Infinity;
              }

              return lodashGet(row, getDataProp(line.value), Infinity);
            }),
          );
        }, Infinity),
  };
};
